import { ref, push, set, remove, update } from 'firebase/database';
import { database } from '../config/firebase';

/**
 * クイズ管理に関するFirebase操作を集約したサービス
 */
export const quizService = {
  /**
   * 新しいクイズを追加
   * @param {string} contentId コンテンツID
   * @param {Object} quizData クイズデータ
   * @returns {Promise<string>} 作成されたクイズのID
   */
  addQuiz: async (contentId, quizData) => {
    const quizRef = push(ref(database, `contents/${contentId}/quizzes`));
    await set(quizRef, {
      ...quizData,
      timestamp: Date.now()
    });
    return quizRef.key;
  },

  /**
   * クイズを更新
   * @param {string} contentId コンテンツID
   * @param {string} quizId クイズID
   * @param {Object} updates 更新データ
   * @returns {Promise<void>}
   */
  updateQuiz: async (contentId, quizId, updates) => {
    const quizRef = ref(database, `contents/${contentId}/quizzes/${quizId}`);
    await update(quizRef, {
      ...updates,
      updatedAt: Date.now()
    });
  },

  /**
   * クイズを削除
   * @param {string} contentId コンテンツID
   * @param {string} quizId クイズID
   * @returns {Promise<void>}
   */
  deleteQuiz: async (contentId, quizId) => {
    const quizRef = ref(database, `contents/${contentId}/quizzes/${quizId}`);
    await remove(quizRef);
  },

  /**
   * クイズの初期データを作成
   * @param {Object} params パラメータオブジェクト
   * @returns {Object} クイズデータ
   */
  createQuizData: ({ 
    type = 'multiple-choice',
    subject = '',
    question = '',
    choices = ['', '', '', ''],
    answer = '',
    explanation = '',
    englishSentence = '',
    japaneseTranslation = '',
    leftItems = [],
    rightItems = [],
    correctMatches = [],
    questionImage = null  // 画像データ（オプショナル）
  }) => {
    const baseData = {
      type,
      subject,
      question,
      choices,
      answer,
      explanation,
      englishSentence,
      japaneseTranslation,
      leftItems,
      rightItems,
      correctMatches
    };

    // 画像データがある場合のみ追加（後方互換性を保つ）
    if (questionImage) {
      baseData.questionImage = questionImage;
      baseData.hasImage = true;
    }

    return baseData;
  },

  /**
   * クイズデータの検証
   * @param {Object} quizData クイズデータ
   * @returns {Object} エラーオブジェクト
   */
  validateQuizData: (quizData) => {
    const errors = {};

    if (!quizData.subject) {
      errors.subject = '教科を選択してください。';
    }

    switch (quizData.type) {
      case 'multiple-choice':
        // 画像がある場合は問題文は任意、画像がない場合は必須
        if (!quizData.hasImage && !quizData.question?.trim()) {
          errors.question = '問題文を入力してください（画像がない場合は必須）。';
        }
        // 画像も問題文もない場合はエラー
        if (!quizData.hasImage && !quizData.question?.trim()) {
          errors.question = '問題文または画像のいずれかを設定してください。';
        }
        if (!quizData.choices || quizData.choices.some(choice => !choice.trim())) {
          errors.choices = 'すべての選択肢を入力してください。';
        }
        if (!quizData.answer?.trim()) {
          errors.answer = '正解を入力してください。';
        }
        break;

      case 'sentence-rearrangement':
        if (!quizData.englishSentence?.trim()) {
          errors.englishSentence = '英語の文を入力してください。';
        }
        if (!quizData.japaneseTranslation?.trim()) {
          errors.japaneseTranslation = '日本語訳を入力してください。';
        }
        break;

      case 'true-false':
        if (!quizData.question?.trim()) {
          errors.question = '問題文を入力してください。';
        }
        if (!quizData.answer?.trim()) {
          errors.answer = '正解を選択してください。';
        }
        break;

      case 'fill-in-the-blank':
        if (!quizData.question?.trim()) {
          errors.question = '問題文を入力してください。';
        }
        if (!quizData.answer?.trim()) {
          errors.answer = '正解を入力してください。';
        }
        break;

      case 'matching':
        if (!quizData.question?.trim()) {
          errors.question = '問題文を入力してください。';
        }
        if (!quizData.leftItems || quizData.leftItems.length === 0 || quizData.leftItems.some(item => !item.trim())) {
          errors.leftItems = '左側の項目をすべて入力してください。';
        }
        if (!quizData.rightItems || quizData.rightItems.length === 0 || quizData.rightItems.some(item => !item.trim())) {
          errors.rightItems = '右側の項目をすべて入力してください。';
        }
        if (!quizData.correctMatches || quizData.correctMatches.length === 0 || quizData.correctMatches.some(match => match === '' || match === undefined)) {
          errors.correctMatches = '正解のマッチングをすべて設定してください。';
        }
        break;

      default:
        errors.type = '有効なクイズ形式を選択してください。';
    }

    return errors;
  }
}; 